/**
 * HTTP Client with Timeout, Request ID, and Error Handling
 */

import { errors } from "com";
import { REQUEST_TIMEOUT, REQUEST_HEADERS } from "./constants";
import { generateRequestId } from "./requestId";

const { SystemError } = errors;

export interface FetchOptions extends RequestInit {
  timeout?: number;
}

/**
 * Handle network and timeout errors
 */
function handleFetchError(error: unknown): never {
  if (error instanceof Error) {
    if (error.name === "AbortError") {
      throw new SystemError("Request timeout. Please check your connection.");
    }
    if (error.message.includes("Failed to fetch")) {
      throw new SystemError("Network error. Please check your connection.");
    }
  }
  throw error;
}

/**
 * Check HTTP status and throw appropriate errors
 */
function checkHttpStatus(response: Response): void {
  if (!response.ok) {
    if (response.status === 429) {
      throw new SystemError("Too many requests. Please try again later.");
    }
    // Let 4xx errors be handled by response processing
  }
}

/**
 * Fetch with timeout, request ID, and error handling
 */
export async function fetchWithTimeout(url: string, options: FetchOptions = {}): Promise<Response> {
  const { timeout = REQUEST_TIMEOUT, headers = {}, ...restOptions } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...restOptions,
      headers: {
        ...REQUEST_HEADERS,
        "X-Request-ID": generateRequestId(),
        ...headers,
      },
      signal: controller.signal,
      credentials: "include",
    });

    clearTimeout(timeoutId);
    checkHttpStatus(response);

    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    return handleFetchError(error);
  }
}
